import { Card, CardContent, makeStyles, useMediaQuery } from '@material-ui/core';
import { Brightness2, BrightnessHigh, Directions, DirectionsTwoTone, Satellite, Streetview, Visibility } from '@material-ui/icons';
import { SpeedDial, SpeedDialAction } from '@material-ui/lab';
import Pin from 'components/Pin';

import { isEmpty } from "lodash";
import mapboxgl from 'mapbox-gl/dist/mapbox-gl';
import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapGL, { AttributionControl, Marker } from 'react-map-gl';
import { useHistory } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { canMessage } from 'util/bookingStates';
import { bookingState, clientState } from '../RecoilState';
import { Provider } from './BookingDetail/Provider';


mapboxgl.workerClass = MapboxWorker;

const MAP_BOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const mapStyles = [
  { label: 'Street', icon: <Streetview />, url: 'mapbox://styles/mapbox/streets-v11' },
  { label: 'Light', icon: <BrightnessHigh />, url: 'mapbox://styles/mapbox/light-v10' },
  { label: 'Dark', icon: <Brightness2 />, url: 'mapbox://styles/mapbox/dark-v10' },
  { label: 'Satellite', icon: <Satellite />, url: 'mapbox://styles/mapbox/satellite-streets-v11' },
  { label: 'Traffic Light', icon: <DirectionsTwoTone />, url: 'mapbox://styles/mapbox/navigation-day-v1' },
  { label: 'Traffic Dark', icon: <Directions />, url: 'mapbox://styles/mapbox/navigation-night-v1' },

]

const useStyles = makeStyles((theme) => {
  return {
    mapLegendMenu: {},
    mapLegendTitle: {
      padding: 3,
      marginLeft: 5,
    },
    mapLegendIcon: {
      width: 30,
    },
    mapLegendIconOther: {
      width: 30,
      filter: 'drop-shadow(-2px 0px 0px #FFF)',
    },
    legendFlyoutButton: {},
    legendBox: {
      position: 'absolute',
      top: 0,
      left: 0,
      display: 'flex',
    },
    root: {
      width: "100%",
      height: "100%",
      position: "relative",
      padding: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        padding: 0,
      },
    },
    mapContainer: {
      height: "100%",
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down("sm")]: {
        padding: 0,
        margin: 0
      },
    },
    mapBox: {
      flex: 1,
    },
    providerBox: {
      position: 'absolute',
      // height: '150px',
      bottom: theme.spacing(3),
      margin: theme.spacing(2),
      maxWidth: `45%`,
      minWidth: `40%`,
      [theme.breakpoints.down("sm")]: {
        maxWidth: 'unset',
        minWidth: 'unset',
        width: `calc(100% - ${theme.spacing(2)}px)`,
        margin: theme.spacing(1),
        height: 'auto',
      },
    },
    tooltip: {
      fontSize: '3em',
      color: 'rgba(0, 0, 0, 0.87)',
    },
  }
})
const mapStyle = {
  width: '100%',
  height: '100%'
};

const INIT_CENTER = {
  geometry: {
    coordinates: [14.58, 121.0]
  }
}
const BookingMap = ({ providerCardData, bounds, serviceId, centerPoint = INIT_CENTER, markerData = [], children }) => {
  const classes = useStyles();

  const history = useHistory()

  const mapRef = useRef();

  const [loading, setLoading] = useState(false);

  const [currentMapStyle, setCurrentMapStyle] = useState(mapStyles[0].url);

  const [hasMapLoaded, setHasMapLoaded] = useState(false);

  const [themeOpen, setThemeOpen] = useState(false);
  const booking = useRecoilValue(bookingState)
  const clientStatus = useRecoilValue(clientState)

  useEffect(() => {
    if (!isEmpty(bounds)) {
      mapRef?.current?.fitBounds(bounds,
        {
          padding: {
            left: 75,
            right: 75,
            top: 75,
            bottom: 300
          }, duration: 1000
        }
      );
      setHasMapLoaded(true)
    }
  }, [bounds]);

  const markers = useMemo(() => {
    if (isEmpty(markerData)) return <></>
    return markerData?.map((item, index) => {
      return (
        <Marker key={`mk-${index}`} longitude={item.longitude} latitude={item.latitude} anchor="bottom">
          <Pin item={item} />
        </Marker>
      )
    })


  }, [markerData]);

  const viewPort = useMemo(() => {
    

    const rcp = hasMapLoaded ? {} : {
      zoom: 5,
      bearing: 0,
      pitch: 0,
      
    }
    return rcp
  }, [centerPoint, hasMapLoaded]);

  const isMobile = useMediaQuery('(min-width:600px)');

  const goToProfile = useCallback(
    () => {
      history.push(`/bookings/${serviceId}/providerProfile` + history.location.search, { from: 'booking' })
    },
    [history, serviceId],
  );
  return (
    <Card className={classes.root}>
      <CardContent className={classes.mapContainer}>
        <MapGL
          reuseMaps
          style={mapStyle}
          touchZoomRotate={isMobile}
          dragPan={isMobile}
          ref={mapRef}
          mapStyle={currentMapStyle}
          mapboxAccessToken={MAP_BOX_TOKEN}
          attributionControl={false}
          {...viewPort}
        >
          {markers}
          <AttributionControl compact position='top-right' />
        </MapGL>
        <div className={classes.providerBox}>
          <Provider provider={providerCardData}
            screen="Map"
            enableMessaging={canMessage(booking, clientStatus)}
            onShowBookingDetails={() => history.push(`/bookings/${serviceId}`, { from: 'map' })}
            onClickProfile={() => goToProfile()} />
        </div>
      </CardContent>
      <div className={classes.legendBox}>
        <SpeedDial
          ariaLabel="Theme"
          FabProps={{
            size: "medium"
          }}
          className={classes.speedDial}
          icon={<Visibility />}

          onClick={() => setThemeOpen(!themeOpen)}
          open={themeOpen}
          direction="down"
        >
          {mapStyles.map((action) => (
            <SpeedDialAction
              key={action.label}
              icon={action.icon}
              tooltipTitle={action.label}
              onClick={() => setCurrentMapStyle(action.url)}
            />
          ))}
        </SpeedDial>
      </div>
    </Card>
  );
};

export { BookingMap };
